/**
 * 司机追加订单弹窗组件
 * 支持AI识别和手动录入两种方式
 */
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DriverAPI } from './config';
import { extraVehicleTypes, normalizeExtraVehicle, extraDetailDefaults, extraDetailNumbers, extraDetailsFromSchedule, extraDetailsPayload, getExtraScheduleText } from './extraScheduleFields';
import { getDriverLanguage } from './driverI18n';
import { getDriverFeatureText, getLocalizedDriverApiError } from './driverFeatureI18n';
import InfantSeatSelector from '../../components/InfantSeatSelector';

export default function ExtraScheduleModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  editingSchedule = null,
  api = DriverAPI 
}) {
  const language = getDriverLanguage();
  const detailCopy = getExtraScheduleText(language);
  const [defaultVehicle, setDefaultVehicle] = useState(undefined);
  const [vehicleDraft, setVehicleDraft] = useState('');
  const [vehicleSaving, setVehicleSaving] = useState(false);
  const [vehicleError, setVehicleError] = useState(false);
  const [vehicleLoadAttempt, setVehicleLoadAttempt] = useState(0);
  const [seatRequired, setSeatRequired] = useState(false);
  const copy = getDriverFeatureText(language).extraSchedule;
  const currencyOptions = [
    { value: 'JPY', label: copy.jpy, symbol: '¥' },
    { value: 'CNY', label: copy.cny, symbol: '¥' },
  ];
  // 表单状态
  const [form, setForm] = useState({
    ...extraDetailDefaults,
    raw_text: '',
    date: '',
    time: '',
    time_raw: '',
    pickup_location: '',
    dropoff_location: '',
    price_amount: '',
    price_currency: 'JPY',
    service_type: '',
    vehicle_type: '',
    notes: '',
  });
  
  // AI解析状态
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [parseError, setParseError] = useState('');
  
  // 查重状态
  const [duplicates, setDuplicates] = useState([]);
  const [duplicateChecked, setDuplicateChecked] = useState(false);
  const [duplicateConfirmed, setDuplicateConfirmed] = useState(false);
  
  // 提交状态
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // 编辑模式初始化
  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    if (editingSchedule) {
      setSeatRequired(Number(editingSchedule.child_seat_count) > 0);
      setForm({
        ...extraDetailsFromSchedule(editingSchedule),
        raw_text: editingSchedule.raw_text || '',
        date: editingSchedule.date || '',
        time: editingSchedule.time || '',
        time_raw: editingSchedule.time_raw || '',
        pickup_location: editingSchedule.pickup_location || '',
        dropoff_location: editingSchedule.dropoff_location || '',
        price_amount: editingSchedule.price_amount?.toString() || '',
        price_currency: editingSchedule.price_currency || 'JPY',
        service_type: editingSchedule.service_type || '',
        vehicle_type: normalizeExtraVehicle(editingSchedule.vehicle_type),
        notes: editingSchedule.notes || '',
      });
      setDuplicateChecked(true);
      setDuplicateConfirmed(true);
    } else {
      resetForm();
      setDefaultVehicle(undefined);
      setVehicleDraft('');
      setVehicleError(false);
      api.getExtraSchedulePreferences().then(result => {
        if (!active) return;
        const vehicle = normalizeExtraVehicle(result.default_vehicle_type);
        setDefaultVehicle(vehicle || null);
        setForm(prev => ({ ...prev, vehicle_type: vehicle }));
      }).catch(() => { if (active) setVehicleError(true); });
    }
    return () => { active = false; };
  }, [api, editingSchedule, isOpen, vehicleLoadAttempt]);

  const saveDefaultVehicle = async vehicle => {
    if (!extraVehicleTypes.includes(vehicle) || vehicleSaving) return;
    setVehicleSaving(true);
    setVehicleError(false);
    try {
      const result = await api.saveExtraSchedulePreferences(vehicle);
      if (result.default_vehicle_type !== vehicle) throw new Error('Preference not saved');
      setDefaultVehicle(vehicle);
      setForm(prev => ({ ...prev, vehicle_type: vehicle }));
    } catch (_) { setVehicleError(true); }
    finally { setVehicleSaving(false); }
  };
  
  const resetForm = () => {
    setSeatRequired(false);
    setForm({
      ...extraDetailDefaults,
    raw_text: '',
      date: '',
      time: '',
      time_raw: '',
      pickup_location: '',
      dropoff_location: '',
      price_amount: '',
      price_currency: 'JPY',
      service_type: '',
      vehicle_type: '',
      notes: '',
    });
    setParseResult(null);
    setParseError('');
    setDuplicates([]);
    setDuplicateChecked(false);
    setDuplicateConfirmed(false);
    setError('');
  };
  
  // AI识别
  const handleParse = async () => {
    if (!form.raw_text.trim()) {
      setParseError(copy.rawRequired);
      return;
    }
    
    setParsing(true);
    setParseError('');
    setParseResult(null);
    
    try {
      const result = await api.parseExtraSchedule(form.raw_text);
      
      if (result.success && result.parsed) {
        setParseResult(result);
        
        // 填充解析结果到表单
        const parsed = result.parsed;
        if (parsed.child_seat_count != null) setSeatRequired(Number(parsed.child_seat_count) > 0);
        setForm(prev => ({
          ...prev,
          ...Object.fromEntries(Object.keys(extraDetailDefaults).filter(key => parsed[key] != null).map(key => [key, parsed[key]])),
          selected_hours: parsed.service_type === 'transfer' ? '' : (parsed.selected_hours ?? prev.selected_hours),
          date: parsed.date || prev.date,
          time: parsed.time || prev.time,
          time_raw: parsed.time_raw || prev.time_raw,
          pickup_location: parsed.pickup_location || prev.pickup_location,
          dropoff_location: parsed.dropoff_location || prev.dropoff_location,
          price_amount: parsed.price_amount?.toString() || prev.price_amount,
          price_currency: parsed.price_currency || prev.price_currency,
          service_type: parsed.service_type || prev.service_type,
          vehicle_type: normalizeExtraVehicle(parsed.vehicle_type) || prev.vehicle_type,
          notes: parsed.notes || prev.notes,
        }));
        
        // 显示缺失字段提示
        if (result.missing_fields && result.missing_fields.length > 0) {
          const fieldNames = { ...copy.missingFieldNames, ...detailCopy };
          const missingNames = result.missing_fields.map(f => fieldNames[f] || f).join('、');
          setParseError(copy.missingFields(missingNames));
        }
      } else {
        setParseError(copy.parseFailed);
      }
    } catch (err) {
      console.error('Parse error:', err);
      setParseError(getLocalizedDriverApiError(err, copy.parseFailed, language));
    } finally {
      setParsing(false);
    }
  };
  
  // 查重检查
  const handleCheckDuplicate = useCallback(async () => {
    if (!form.date || !form.time || !form.pickup_location || !form.dropoff_location) {
      return;
    }
    
    try {
      const result = await api.checkDuplicate({
        date: form.date,
        time: form.time,
        pickup_location: form.pickup_location,
        dropoff_location: form.dropoff_location,
        exclude_id: editingSchedule?.id || null
      });
      
      setDuplicates(result.duplicates || []);
      setDuplicateChecked(true);
      
      if (!result.has_duplicates) {
        setDuplicateConfirmed(true);
      }
    } catch (err) {
      console.error('Duplicate check error:', err);
      setDuplicateChecked(true);
      setDuplicateConfirmed(true);
    }
  }, [api, editingSchedule?.id, form.date, form.dropoff_location, form.pickup_location, form.time]);
  
  // 当关键字段变化时触发查重
  useEffect(() => {
    if (form.date && form.time && form.pickup_location && form.dropoff_location) {
      setDuplicateChecked(false);
      setDuplicateConfirmed(false);
      const timer = setTimeout(() => {
        handleCheckDuplicate();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [form.date, form.time, form.pickup_location, form.dropoff_location, handleCheckDuplicate]);
  
  // 验证表单
  const validateForm = () => {
    if (!extraVehicleTypes.includes(form.vehicle_type)) return detailCopy.chooseVehicle;
    if (!['charter', 'transfer'].includes(form.service_type)) return detailCopy.chooseService;
    if (form.service_type === 'charter' && (!Number.isFinite(Number(form.selected_hours)) || Number(form.selected_hours) <= 0)) return detailCopy.invalidHours;
    if (extraDetailNumbers.filter(key => key !== 'selected_hours').some(key => form[key] !== '' && (!Number.isInteger(Number(form[key])) || Number(form[key]) < 0))) return detailCopy.invalidCount;
    if (seatRequired && !(Number(form.child_seat_count) > 0)) return detailCopy.invalidCount;

    if (!form.date) return copy.chooseDate;
    if (!form.time) return copy.chooseTime;
    if (!/^\d{2}:\d{2}$/.test(form.time)) return copy.invalidTime;
    if (!form.pickup_location.trim()) return copy.enterPickup;
    if (!form.dropoff_location.trim()) return copy.enterDestination;
    const priceAmount = parseFloat(form.price_amount);
    if ((form.price_amount === '' && !api.optionalPrice) || (form.price_amount !== '' && (!Number.isFinite(priceAmount) || priceAmount < 0))) return copy.invalidAmount;
    return null;
  };
  
  // 提交
  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    // 如果有重复且未确认，提示用户
    if (duplicates.length > 0 && !duplicateConfirmed) {
      setError(copy.duplicateRequired);
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const submitData = {
        ...extraDetailsPayload(form),
        raw_text: form.raw_text || null,
        date: form.date,
        time: form.time,
        time_raw: form.time_raw || null,
        pickup_location: form.pickup_location.trim(),
        dropoff_location: form.dropoff_location.trim(),
        price_amount: form.price_amount === '' ? null : parseFloat(form.price_amount),
        price_currency: form.price_currency,
        service_type: form.service_type || null,
        vehicle_type: form.vehicle_type || null,
        notes: form.notes || null,
        parse_status: parseResult?.parse_status || null,
        parse_confidence: parseResult?.confidence || null,
        parse_model: parseResult?.parse_model || null,
        duplicate_checked: duplicateChecked,
        duplicate_confirmed: duplicateConfirmed,
      };
      
      if (!isOpen) return;
    let active = true;
    if (editingSchedule) {
        await api.updateExtraSchedule(editingSchedule.id, submitData);
      } else {
        await api.createExtraSchedule(submitData);
      }
      
      onSuccess?.();
      onClose();
      resetForm();
    } catch (err) {
      console.error('Submit error:', err);
      setError(getLocalizedDriverApiError(err, copy.saveFailed, language));
    } finally {
      setSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  if (!editingSchedule && !defaultVehicle) return createPortal(
    <div className="driver-shared-modal fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4">
      <div role="dialog" aria-modal="true" aria-label={detailCopy.vehicleTitle} className="bg-white rounded-2xl p-6 w-full max-w-md space-y-4 text-gray-900">
        <h2 className="text-lg font-semibold">{detailCopy.vehicleTitle}</h2>
        <p>{detailCopy.vehicleHint}</p>
        {defaultVehicle === null && <>
          <select aria-label={detailCopy.vehicle_type} data-testid="default-vehicle-select" className="w-full border rounded-lg p-3" value={vehicleDraft} onChange={e => setVehicleDraft(e.target.value)} disabled={vehicleSaving}>
            <option value="" disabled>{detailCopy.chooseVehicle}</option>
            {extraVehicleTypes.map(value => <option key={value} value={value}>{detailCopy[value]}</option>)}
          </select>
          <button className="w-full bg-blue-600 text-white rounded-lg p-3 disabled:opacity-50" disabled={!vehicleDraft || vehicleSaving} onClick={() => saveDefaultVehicle(vehicleDraft)}>{vehicleSaving ? detailCopy.saving : detailCopy.saveVehicle}</button>
        </>}
        {defaultVehicle === undefined && !vehicleError && <p role="status">…</p>}
        {vehicleError && <p role="alert">{detailCopy.failed}</p>}
        {vehicleError && defaultVehicle === undefined && <button onClick={() => setVehicleLoadAttempt(n => n + 1)}>{detailCopy.failed}</button>}
        <button onClick={onClose} disabled={vehicleSaving}>×</button>
      </div>
    </div>, document.body);

  
  return createPortal(
    <div className="driver-shared-modal fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
      <div className="premium-card w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card p-4 border-b flex justify-between items-center z-10">
          <h3 className="text-lg font-bold text-foreground">
            {editingSchedule ? copy.titleEdit : copy.titleAdd}
          </h3>
          <button
            onClick={() => { onClose(); resetForm(); }}
            className="text-muted-foreground hover:text-foreground text-2xl leading-none"
            data-testid="close-extra-modal"
          >
            ×
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          {/* AI识别区域 */}
          {!editingSchedule && (
            <div className="premium-section p-4">
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                {copy.aiTitle}
              </label>
              <textarea
                value={form.raw_text}
                onChange={(e) => { setForm({ ...form, raw_text: e.target.value }); }}
                placeholder={copy.rawPlaceholder}
                className="premium-input resize-none"
                rows={8}
                style={{minHeight:200,maxHeight:400,overflowY:'auto'}}
                data-testid="raw-text-input"
              />
              <button
                onClick={handleParse}
                disabled={parsing || !form.raw_text.trim()}
                className="mt-2 w-full py-2 bg-primary text-primary-foreground rounded-xl font-medium hover:brightness-110 disabled:opacity-50 transition"
                data-testid="parse-btn"
              >
                {parsing ? copy.parsing : copy.parse}
              </button>
              
              {parseError && (
                <p className="mt-2 text-sm text-warning-foreground">{parseError}</p>
              )}
              
              {parseResult?.parse_status === 'success' && (
                <p className="mt-2 text-sm text-success">✓ {copy.parseDone}</p>
              )}
            </div>
          )}
          
          {/* 分隔线 */}
          {!editingSchedule && (
            <div className="flex items-center gap-3 text-muted-foreground text-sm">
              <div className="flex-1 border-t"></div>
              <span>{copy.manual}</span>
              <div className="flex-1 border-t"></div>
            </div>
          )}
          
          {/* 表单字段 */}
          <div className="space-y-4">
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">{detailCopy.service_type} *</legend>
              <div className="grid grid-cols-2 gap-3">
                {['charter', 'transfer'].map(value => <button key={value} type="button" aria-pressed={form.service_type === value}
                  className={`py-3 rounded-xl border ${form.service_type === value ? 'bg-primary text-primary-foreground' : 'border-border-subtle'}`}
                  onClick={() => setForm(current => ({ ...current, service_type: value, selected_hours: value === 'transfer' ? '' : current.selected_hours }))}>
                  {detailCopy[value]}</button>)}
              </div>
            </fieldset>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">{detailCopy.vehicle_type}
                <select data-testid="extra-vehicle_type" className="w-full border rounded-lg p-3 bg-white" value={form.vehicle_type} onChange={e => setForm(prev => ({ ...prev, vehicle_type: e.target.value }))}>
                  <option value="" disabled>{detailCopy.chooseVehicle}</option>
                  {extraVehicleTypes.map(value => <option key={value} value={value}>{detailCopy[value]}</option>)}
                </select>
              </label>
              {!editingSchedule && <div className="text-sm">
                <span>{detailCopy.defaultVehicle}: {detailCopy[defaultVehicle]}</span>
                {form.vehicle_type !== defaultVehicle && <button type="button" disabled={vehicleSaving} className="ml-2 text-blue-600" onClick={() => saveDefaultVehicle(form.vehicle_type)}>{vehicleSaving ? detailCopy.saving : detailCopy.makeDefaultVehicle}</button>}
                {vehicleError && <p role="alert">{detailCopy.failed}</p>}
              </div>}
              {['selected_hours', 'passenger_count', 'luggage_count', 'customer_name', 'phone', 'flight_number'].filter(key => key !== 'selected_hours' || form.service_type === 'charter').map(key => (
                <label key={key} className="block text-sm font-medium">{detailCopy[key]}
                  <input className="premium-input mt-1" data-testid={`extra-${key}`} value={form[key]}
                    type={extraDetailNumbers.includes(key) ? 'number' : key === 'phone' ? 'tel' : 'text'}
                    min={key === 'selected_hours' ? '0.1' : '0'} step={key === 'selected_hours' ? 'any' : '1'}
                    onChange={event => setForm(current => ({ ...current, [key]: event.target.value }))} />
                </label>
              ))}
            </div>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">{detailCopy.child_seat_count}</legend>
              <div className="flex gap-2">
                {['unknown', 'no', 'yes'].map(value => <button key={value} type="button"
                  aria-pressed={value === 'yes' ? seatRequired : value === 'no' ? !seatRequired && form.child_seat_count === 0 : !seatRequired && form.child_seat_count === ''}
                  className="premium-chip aria-pressed:bg-primary aria-pressed:text-primary-foreground px-4 py-2"
                  onClick={() => { setSeatRequired(value === 'yes'); setForm(current => ({ ...current, child_seat_count: value === 'no' ? 0 : '' })); }}>
                  {detailCopy[value]}</button>)}
              </div>
              {seatRequired && <label className="block text-sm">{detailCopy.seatCount}
                <input className="premium-input mt-1" type="number" min="1" step="1" value={form.child_seat_count}
                  onChange={event => setForm(current => ({ ...current, child_seat_count: event.target.value }))} />
              </label>}
            </fieldset>
            <InfantSeatSelector language={language} value={form.infant_seat_count ?? 0} onChange={infant_seat_count => setForm(current => ({ ...current, infant_seat_count }))} />
            {/* 日期时间 */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {copy.date} <span className="text-destructive">*</span>
                </label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="premium-input"
                  data-testid="date-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {copy.time} <span className="text-destructive">*</span>
                </label>
                <input
                  type="time"
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                  className="premium-input"
                  data-testid="time-input"
                />
              </div>
            </div>
            
            {/* 出发地 */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {copy.pickup} <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={form.pickup_location}
                onChange={(e) => setForm({ ...form, pickup_location: e.target.value })}
                placeholder={copy.pickupPlaceholder}
                className="premium-input"
                data-testid="pickup-input"
              />
            </div>
            
            {/* 目的地 */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {copy.destination} <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={form.dropoff_location}
                onChange={(e) => setForm({ ...form, dropoff_location: e.target.value })}
                placeholder={copy.destinationPlaceholder}
                className="premium-input"
                data-testid="dropoff-input"
              />
            </div>
            
            {/* 金额和币种 */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {copy.amount} {!api.optionalPrice&&<span className="text-destructive">*</span>}
                </label>
                <input
                  type="number"
                  value={form.price_amount}
                  onChange={(e) => setForm({ ...form, price_amount: e.target.value })}
                  min="0"
                  className="premium-input"
                  data-testid="price-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {copy.currency}
                </label>
                <select
                  value={form.price_currency}
                  onChange={(e) => setForm({ ...form, price_currency: e.target.value })}
                  className="premium-input"
                  data-testid="currency-select"
                >
                  {currencyOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* 备注 */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {copy.notes}
              </label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder={copy.optional}
                className="premium-input resize-none"
                rows={2}
                data-testid="notes-input"
              />
            </div>
          </div>
          
          {/* 查重提醒 */}
          {duplicates.length > 0 && !duplicateConfirmed && (
            <div className="premium-chip-warning rounded-xl p-3">
              <p className="font-medium text-sm mb-2">
                {copy.duplicateWarning}
              </p>
              <ul className="text-sm text-warning-foreground space-y-1 mb-3">
                {duplicates.map((d, i) => (
                  <li key={i}>
                    • {d.date} {d.time} {d.pickup_location} → {d.dropoff_location}
                    <span className="text-warning-foreground text-xs ml-1">({d.reasons?.join('、')})</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setDuplicateConfirmed(true)}
                className="w-full py-2 bg-warning text-warning-foreground rounded-xl text-sm font-medium hover:brightness-110 transition"
                data-testid="confirm-not-duplicate"
              >
                {copy.duplicateConfirm}
              </button>
            </div>
          )}
          
          {duplicateConfirmed && duplicates.length > 0 && (
            <p className="text-sm text-success">✓ {copy.duplicateConfirmed}</p>
          )}
          
          {/* 错误提示 */}
          {error && (
            <div className="premium-chip-destructive rounded-lg p-3 text-sm">
              {error}
            </div>
          )}
          
          {/* 操作按钮 */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => { onClose(); resetForm(); }}
                className="flex-1 py-3 border border-border-subtle rounded-xl font-medium hover:bg-surface transition"
              data-testid="cancel-btn"
            >
              {copy.cancel}
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:brightness-110 disabled:opacity-50 transition"
              data-testid="submit-btn"
            >
              {submitting ? copy.saving : (editingSchedule ? copy.saveEdit : copy.save)}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
